import { useState, useEffect, useCallback } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import { IconRefresh, IconX } from '@tabler/icons-react';

/** How long to wait before reminding the user again after they dismiss. */
const REMIND_AGAIN_AFTER_MS = 2 * 60 * 1000;

/**
 * Displays a persistent snackbar notification at the bottom of the screen whenever a new Service Worker version is available.
 */
const ServiceWorkerUpdateBanner = () => {
  const {
    needRefresh: [needRefresh],
    updateServiceWorker
  } = useRegisterSW();

  const [dismissed, setDismissed] = useState(false);

  // When the user dismisses, schedule a reminder after the delay.
  useEffect(() => {
    if (!dismissed) return;
    const timer = setTimeout(() => setDismissed(false), REMIND_AGAIN_AFTER_MS);
    return () => clearTimeout(timer);
  }, [dismissed]);

  const handleUpdate = useCallback(() => {
    void updateServiceWorker(true);
  }, [updateServiceWorker]);

  const handleDismiss = useCallback(() => {
    setDismissed(true);
  }, [setDismissed]);

  return (
    <Snackbar open={needRefresh && !dismissed} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} sx={{ mb: 2 }}>
      <Alert
        severity="info"
        variant="filled"
        icon={<IconRefresh size={20} />}
        action={
          <>
            <Button
              size="small"
              variant="outlined"
              onClick={handleUpdate}
              sx={{ ml: 1, whiteSpace: 'nowrap', borderColor: 'rgba(255,255,255,0.6)', '&:hover': { borderColor: '#fff' } }}
            >
              Update now
            </Button>
            <IconButton color="inherit" size="small" onClick={handleDismiss} aria-label="Dismiss update notification" sx={{ ml: 1 }}>
              <IconX size={20} />
            </IconButton>
          </>
        }
        sx={{ alignItems: 'center', width: '100%' }}
      >
        <AlertTitle sx={{ mb: 0 }}>Update available</AlertTitle>A new version of the app is ready. Reload to get the latest features.
      </Alert>
    </Snackbar>
  );
};

export default ServiceWorkerUpdateBanner;
